// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Implement miscellaneous extra product.
 * @author James Reid
 */

// @ts-check

// @@imports-module
import { Product } from "./Product.js"

// @@imports-types
import {
    ProductData,
    EXTRA_TYPES,
    PRODUCT_DISPLAY_CATEGORIES
} from "../types/index.js"

// @@body
class Extra extends Product {
    /**
     * Construct miscellaneous extra product from data returned from server.
     * @param {ProductData} data Product data returned from server.
     * @param {any} match Regexp match data for internal calculator sku code
     * pertaining to this extended class.
     */
    constructor(data, match) {
        super(data)

        // Parse regexp data.
        const { type, category, code, width } =
            /** @type {Object<string, string>} */ (match.groups)

        if (category && code) {
            // Set extended class values for extra product.
            this.type = type === "C" ? EXTRA_TYPES.cleaners
                : type === "J" ? EXTRA_TYPES.joiners
                : type === "F" ? EXTRA_TYPES.fixings
                : category === "RAD" ? EXTRA_TYPES.tools.adhesiveRollers
                : category === "RRU" ? EXTRA_TYPES.tools.rubberRollers
                : category === "BRU" ? EXTRA_TYPES.tools.brushes
                : category === "GUN" ? EXTRA_TYPES.tools.guns
                : category === "SCI" ? EXTRA_TYPES.tools.scissors
                : category === "SCR" ? EXTRA_TYPES.tools.scrubbers
                : category === "KIT" ? EXTRA_TYPES.tools.kits
                : EXTRA_TYPES.tools.other
            this.displayCategory = PRODUCT_DISPLAY_CATEGORIES.extra
        }
        else if (width) {
            // Set extended class values for installation kit.
            this.type = type === "RC" ? EXTRA_TYPES.kits.rooflight
                : type === "RT" ? EXTRA_TYPES.kits.rooflight
                : type === "ID" ? EXTRA_TYPES.kits.internalDrain
                : type === "HD " ? EXTRA_TYPES.kits.horizontalDrain
                : EXTRA_TYPES.kits.pipeSeal
            this.displayCategory = PRODUCT_DISPLAY_CATEGORIES.kit
        }

        // Declare explicit [header, value] detail pairs for products requiring
        // specific named extra information in extras/essentials card quantity
        // selection table.
        this.tableDetail = this.code === "KRC_2286" ? ["Upstand Type", "Plastic - 9\" tape"]
            : this.code === "KRC_3048" ? ["Upstand Type", "Timber - 12\" tape"]
            : this.code === "KHD_0100" ? ["Outlet Dimensions", "100mm x 100mm (w x h)"]
            : this.code === "KHD_0065" ? ["Outlet Dimensions", "100mm x 65mm (w x h)"]
            : this.code === "KID_0068" ? ["Drain Diameter", "68mm"]
            : this.code === "KID_0100" ? ["Drain Diameter", "100mm"]
            : this.code === "KID_0110" ? ["Drain Diameter", "110mm"]
            : this.code === "KPS_0152" ? ["Pipe Diameter", "25mm - 152mm"]
            : ["Name", this.name]
    }
}

// @@exports
export { Extra }
